import {Fragment, memo, useCallback, useState} from 'react'
import {type StyleProp, View, type ViewStyle} from 'react-native'
import {type ModerationDecision} from '@bsky/sdk/moderation'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {useQueryClient} from '@tanstack/react-query'

import {makeProfileLink} from '#/lib/routes/links'
import {forceLTR} from '#/lib/strings/bidi'
import {NON_BREAKING_SPACE} from '#/lib/strings/constants'
import {getAuthorPrimaryName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {niceDate} from '#/lib/strings/time'
import {userStyle} from '#/lib/userstyles'
import {useProfileShadow} from '#/state/cache/profile-shadow'
import {useHideDisplayNames} from '#/state/preferences/hide-display-names'
import {unstableCacheProfileView} from '#/state/queries/profile'
import {atoms as a, platform, useTheme, web} from '#/alf'
import {WebOnlyInlineLinkText} from '#/components/Link'
import {ProfileBadgesFromProfileShadow} from '#/components/ProfileBadges'
import {ProfileHoverCard} from '#/components/ProfileHoverCard'
import {PronounPill} from '#/components/PronounPill'
import {Text} from '#/components/Typography'
import {IS_ANDROID, IS_WEB} from '#/env'
import {useActorStatus} from '#/features/liveNow'
import {type app} from '#/lexicons'
import {TimeElapsed} from './TimeElapsed'
import {PreviewableUserAvatar} from './UserAvatar'

interface PostMetaOpts {
  author: app.bsky.actor.defs.ProfileViewBasic
  moderation: ModerationDecision | undefined
  postHref: string
  timestamp: string
  linkDisabled?: boolean
  narrowLayout?: boolean
  constrainWidth?: boolean
  showAvatar?: boolean
  showPronouns?: boolean
  avatarSize?: number
  onOpenAuthor?: () => void
  style?: StyleProp<ViewStyle>
}

let PostMeta = (opts: PostMetaOpts): React.ReactNode => {
  const t = useTheme()
  const [authorRowHeight, setAuthorRowHeight] = useState<number>()
  const {i18n, _} = useLingui()

  const author = useProfileShadow(opts.author)
  const hideDisplayNames = useHideDisplayNames()
  const displayName = getAuthorPrimaryName(author, {
    hideDisplayNames,
    moderation: opts.moderation?.ui('displayName'),
  })
  const handle = author.handle
  // remove dumb typing when you update the atproto api package!!
  const pronouns = (author as {pronouns?: string})?.pronouns
  const profileLink = makeProfileLink(author)
  const queryClient = useQueryClient()
  const onOpenAuthor = opts.onOpenAuthor
  const onBeforePressAuthor = useCallback(() => {
    unstableCacheProfileView(queryClient, author)
    onOpenAuthor?.()
  }, [queryClient, author, onOpenAuthor])
  const onBeforePressPost = useCallback(() => {
    unstableCacheProfileView(queryClient, author)
  }, [queryClient, author])

  const timestampLabel = niceDate(i18n, opts.timestamp)
  const {isActive: live} = useActorStatus(author)

  // Native hover cards have no wrapper. Keep ordinary author rows as flat as
  // upstream rather than introducing another flex-basis calculation in Yoga.
  const AuthorGroup = !IS_WEB && !opts.showPronouns ? Fragment : View
  const MaybeLinkText = opts.linkDisabled ? Text : WebOnlyInlineLinkText

  return (
    <View
      style={[
        userStyle('wsky-post__meta'),
        IS_WEB ? a.flex_shrink : a.flex_1,
        a.flex_row,
        a.align_center,
        a.pb_xs,
        a.gap_xs,
        a.z_20,
        opts.style,
      ]}>
      {opts.showAvatar && (
        <View style={[a.self_center, a.mr_2xs]}>
          <PreviewableUserAvatar
            size={opts.avatarSize || 16}
            profile={author}
            moderation={opts.moderation?.ui('avatar')}
            type={author.associated?.labeler ? 'labeler' : 'user'}
            live={live}
            hideLiveBadge
            disableNavigation={opts.linkDisabled}
          />
        </View>
      )}
      <View
        style={[
          a.flex_row,
          a.align_end,
          a.flex_shrink,
          web({flex: 1, minWidth: 0}),
        ]}>
        <ProfileHoverCard
          did={author.did}
          style={[a.flex_shrink, {minWidth: 0}, opts.showPronouns && a.flex_1]}>
          <View
            style={[
              a.flex_row,
              a.align_end,
              a.flex_shrink,
              web({flex: 1, minWidth: 0}),
              opts.showPronouns && [
                a.flex_wrap,
                a.overflow_hidden,
                {maxHeight: authorRowHeight},
              ],
            ]}>
            <AuthorGroup
              {...(AuthorGroup === View
                ? {
                    onLayout: opts.showPronouns
                      ? event => {
                          const {height} = event.nativeEvent.layout
                          if (height > 0) setAuthorRowHeight(height)
                        }
                      : undefined,
                    style: [
                      a.flex_row,
                      a.align_end,
                      a.flex_shrink,
                      web({minWidth: 0, maxWidth: '100%'}),
                    ],
                  }
                : {})}>
              <MaybeLinkText
                emoji
                numberOfLines={1}
                to={profileLink}
                label={_(msg`View profile`)}
                disableMismatchWarning
                onPress={opts.linkDisabled ? undefined : onBeforePressAuthor}
                style={[
                  a.text_md,
                  a.font_semi_bold,
                  t.atoms.text,
                  a.leading_tight,
                  // Let the handle shrink first, keeping badges directly after
                  // the display name instead of prematurely truncating it.
                  a.flex_shrink_0,
                  {minWidth: 0, maxWidth: hideDisplayNames ? '100%' : '70%'},
                  web({direction: 'ltr', unicodeBidi: 'isolate'}),
                ]}>
                {forceLTR(displayName)}
              </MaybeLinkText>
              <ProfileBadgesFromProfileShadow
                profile={author}
                size="sm"
                pdsInteractive={false}
                style={[
                  a.pl_2xs,
                  a.self_center,
                  {
                    marginTop: platform({web: 1, ios: 0, android: -1}),
                  },
                ]}
              />
              {!hideDisplayNames && (
                <MaybeLinkText
                  emoji
                  numberOfLines={1}
                  to={profileLink}
                  label={_(msg`View profile`)}
                  disableMismatchWarning
                  disableUnderline
                  onPress={opts.linkDisabled ? undefined : onBeforePressAuthor}
                  style={[
                    a.text_md,
                    t.atoms.text_contrast_medium,
                    {lineHeight: 1.17},
                    {flexShrink: 10, minWidth: 0},
                    web({maxWidth: 'max-content'}),
                  ]}>
                  {NON_BREAKING_SPACE + sanitizeHandle(handle, '@')}
                </MaybeLinkText>
              )}
            </AuthorGroup>
            {opts.showPronouns && (
              <PronounPill
                pronouns={pronouns}
                style={[a.ml_xs, a.self_center, a.flex_shrink_0]}
              />
            )}
          </View>
        </ProfileHoverCard>

        <TimeElapsed timestamp={opts.timestamp}>
          {({timeElapsed}) => (
            <MaybeLinkText
              to={opts.postHref}
              label={timestampLabel}
              title={timestampLabel}
              disableMismatchWarning
              disableUnderline
              onPress={opts.linkDisabled ? undefined : onBeforePressPost}
              style={[
                a.pl_xs,
                a.flex_shrink_0,
                a.text_md,
                a.leading_tight,
                IS_ANDROID && !opts.narrowLayout && a.flex_grow,
                a.text_right,
                t.atoms.text_contrast_medium,
                web({
                  whiteSpace: 'nowrap',
                }),
              ]}>
              {!opts.showPronouns && (
                <>
                  {!IS_ANDROID && (
                    <Text
                      style={[
                        a.text_md,
                        a.leading_tight,
                        t.atoms.text_contrast_medium,
                      ]}
                      accessible={false}>
                      &middot;{' '}
                    </Text>
                  )}
                  {timeElapsed}
                </>
              )}
            </MaybeLinkText>
          )}
        </TimeElapsed>
      </View>
    </View>
  )
}
PostMeta = memo(PostMeta)
export {PostMeta}
